import type { JSX } from 'react'
import type { FlowStep } from '@fitting-room/contracts'
import { LineChoiceStep } from './steps/LineChoiceStep'
import { BookingStep } from './steps/BookingStep'
import { BrowseStep } from './steps/BrowseStep'
import { FittingRoomStep } from './steps/FittingRoomStep'
import { IntakeStep } from './steps/IntakeStep'
import { PreferenceOrderStep } from './steps/PreferenceOrderStep'
import { QuestionStep } from './steps/QuestionStep'
import { RangeFilterStep } from './steps/RangeFilterStep'
import { SizeStep } from './steps/SizeStep'
import { ShortlistStep } from './steps/ShortlistStep'
import { SummaryStep } from './steps/SummaryStep'
import { WelcomeStep } from './steps/WelcomeStep'
import type { FlowContext } from './types'

/**
 * The whole client-side flow engine.
 *
 * It switches on `step.kind` and nothing else. There is no route per screen,
 * no ordered list of question ids, and no knowledge of what any particular
 * step is asking about — a flow with three preference_order steps and a flow
 * with none both render here without a change. Adding a *kind* is the only
 * thing that touches this file; adding a step, a question, an option, an
 * attribute or a whole new vertical does not.
 *
 * The exhaustiveness check at the bottom is load-bearing: if the contract
 * grows a step kind, this stops compiling instead of silently rendering
 * nothing.
 */
export function FlowRenderer({ step, ctx }: { readonly step: FlowStep; readonly ctx: FlowContext }): JSX.Element {
  switch (step.kind) {
    case 'welcome':
      return <WelcomeStep key={step.id} step={step} ctx={ctx} />
    case 'question':
      return <QuestionStep key={step.id} step={step} ctx={ctx} />
    case 'preference_order':
      return <PreferenceOrderStep key={step.id} step={step} ctx={ctx} />
    case 'range_filter':
      return <RangeFilterStep key={step.id} step={step} ctx={ctx} />
    case 'intake':
      return <IntakeStep key={step.id} step={step} ctx={ctx} />
    case 'line_choice':
      return <LineChoiceStep key={step.id} step={step} ctx={ctx} />
    case 'size':
      return <SizeStep key={step.id} step={step} ctx={ctx} />
    case 'browse':
      return <BrowseStep key={step.id} step={step} ctx={ctx} />
    case 'shortlist':
      return <ShortlistStep key={step.id} step={step} ctx={ctx} />
    case 'summary':
      return <SummaryStep key={step.id} step={step} ctx={ctx} />
    case 'fitting_room':
      return <FittingRoomStep key={step.id} step={step} ctx={ctx} />
    case 'booking':
      return <BookingStep key={step.id} step={step} ctx={ctx} />
    default: {
      const exhaustive: never = step
      return <>{String(exhaustive)}</>
    }
  }
}
